import { RefObject, useEffect, useState } from 'react';

interface UseAboveFoldOptions {
  /** Debounce time (ms) for resize callbacks. Use 0 for no debounce. Default 200ms. */
  debounce?: number;
  /** Whether the hook is disabled */
  disabled?: boolean;
}

const isClient = typeof window !== 'undefined';

const useAboveFold = (
  ref: RefObject<HTMLElement | null>,
  options: UseAboveFoldOptions = {},
) => {
  const { disabled = false, debounce = 200 } = options;

  const [isAboveFold, setIsAboveFold] = useState(false);

  useEffect(() => {
    if (!isClient || disabled) return;

    const element = ref?.current;

    if (!element) return;

    const check = () => {
      const rect = element.getBoundingClientRect();
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;

      // Consider above the fold when any part of the element is within the viewport
      const visible = rect.top < viewportHeight && rect.bottom > 0;

      setIsAboveFold(visible);
    };

    // Check on mount and on resize only (no scroll-based recalculation)
    check();

    const timerRef = { current: undefined as number | undefined };

    const scheduleCheck = () => {
      if (debounce > 0) {
        if (typeof timerRef.current !== 'undefined')
          window.clearTimeout(timerRef.current);

        timerRef.current = window.setTimeout(() => {
          timerRef.current = undefined;
          check();
        }, debounce) as unknown as number;
      } else {
        check();
      }
    };

    // Always listen to window resize to handle viewport height changes
    window.addEventListener('resize', scheduleCheck);

    return () => {
      window.removeEventListener('resize', scheduleCheck);

      if (typeof timerRef.current !== 'undefined')
        window.clearTimeout(timerRef.current);
    };
  }, [ref, disabled, debounce]);

  return isAboveFold;
};

export default useAboveFold;
